import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { SequenceLibrary, SequenceTemplate } from '@types'

import { SearchBar } from '@components/SearchBar'
import { Refresh } from '@components/Refresh'
import { NavBar, NavItemType } from '@components/NavBar'

import { useSequenceList } from './sequence-list.hook'

import { Container, GapContainer, NavBarContainer, ScrollContainer, SectionLabel } from './SequenceTemplateList.styled'
import { SequenceTemplateThumb } from '../../SequenceTemplateThumb'

interface Props {
  selected?: SequenceTemplate | SequenceLibrary
  onClick?: (sequence: SequenceTemplate | SequenceLibrary) => void
}

export const SequenceTemplateList = (props: Props): JSX.Element => {
  const { selected, onClick } = props

  const { t } = useTranslation()

  const [search, setSearch] = useState<string>('')

  const { projectId } = useParams()

  const { data, loading, error, refetch } = useSequenceList(projectId || '')

  const isLoading = loading ? 1 : error ? -1 : 0

  const sequenceTemplates = data?.sequenceTemplates || []
  const sequenceLibraries = data?.sequenceLibraries || []
  const multiProjectSequenceTemplates: SequenceTemplate[] =
    data?.projects?.reduce((acc, project) => {
      if (project.id != projectId) {
        return [
          ...acc,
          ...project.sequenceTemplates.map((sequence) => ({
            ...sequence,
            tasks: sequence.tasks.map((task) => ({ ...task, company: undefined })),
          })),
        ]
      }
      return acc
    }, [] as SequenceTemplate[]) || []

  const filteredSequenceTemplates = sequenceTemplates
    .filter((sequence) => sequence.name.toLowerCase().indexOf(search.toLowerCase()) != -1)
    .sort((a, b) => (a.index < b.index ? -1 : 1))

  const filteredSequenceLibraries = sequenceLibraries.filter(
    (sequence) => sequence.name.toLowerCase().indexOf(search.toLowerCase()) != -1,
  )

  const filteredMultiProjectSequenceTemplates = multiProjectSequenceTemplates
    .filter((sequence) => sequence.name.toLowerCase().indexOf(search.toLowerCase()) != -1)
    .sort((a, b) => (a.index < b.index ? -1 : 1))

  const items: NavItemType[] = [
    {
      key: 'all',
      label: t('modules_project-settings_pages_sequences_label_all_sequences'),
      style: {
        width: 45,
      },
    },
    {
      key: 'toolkit',
      label: 'Toolkit©',
      style: {
        width: 55,
      },
    },

    {
      key: 'project',
      label: t('modules_project-settings_pages_sequences_label_current_project'),
      style: {
        width: 55,
      },
    },
    {
      key: 'multi',
      label: t('modules_project-settings_pages_sequences_label_multi_projects'),
      style: {
        width: 95,
      },
    },
  ]

  const [selectedItem, setSelectedItem] = useState<NavItemType>(items[0])

  return (
    <Container>
      <NavBarContainer>
        <NavBar items={items} selected={selectedItem} onClick={setSelectedItem} />
      </NavBarContainer>

      <SearchBar
        className="search-bar"
        placeholder={t('modules_project-settings_pages_sequences_label_search_field')}
        value={search}
        onChange={(value) => setSearch(value)}
      />

      <ScrollContainer>
        <Refresh loading={isLoading} small onRefresh={() => refetch(projectId || '')}>
          {filteredSequenceLibraries.length > 0 && (selectedItem.key == 'all' || selectedItem.key == 'toolkit') ? (
            <>
              {selectedItem.key == 'all' && <SectionLabel>Toolkit©</SectionLabel>}

              {filteredSequenceLibraries.map((sequence) => (
                <GapContainer key={sequence.id}>
                  <SequenceTemplateThumb sequence={sequence} onClick={onClick} selected={selected?.id == sequence.id} />
                </GapContainer>
              ))}

              {filteredSequenceTemplates.length > 0 ? <div style={{ width: '100%', height: 24 }} /> : null}
            </>
          ) : null}

          {filteredSequenceTemplates.length > 0 && (selectedItem.key == 'all' || selectedItem.key == 'project') ? (
            <>
              {selectedItem.key == 'all' && (
                <SectionLabel>{t('modules_project-settings_pages_sequences_label_current_project')}</SectionLabel>
              )}

              {filteredSequenceTemplates.map((sequence) => (
                <GapContainer key={sequence.id}>
                  <SequenceTemplateThumb sequence={sequence} onClick={onClick} selected={selected?.id == sequence.id} />
                </GapContainer>
              ))}
            </>
          ) : null}

          {filteredMultiProjectSequenceTemplates.length > 0 &&
          (selectedItem.key == 'all' || selectedItem.key == 'multi') ? (
            <>
              {selectedItem.key == 'all' && (
                <SectionLabel>{t('modules_project-settings_pages_sequences_label_multi_projects')}</SectionLabel>
              )}

              {filteredMultiProjectSequenceTemplates.map((sequence) => (
                <GapContainer key={sequence.id}>
                  <SequenceTemplateThumb sequence={sequence} onClick={onClick} selected={selected?.id == sequence.id} />
                </GapContainer>
              ))}
            </>
          ) : null}
        </Refresh>
      </ScrollContainer>
    </Container>
  )
}
